import { useState } from 'react'
import { ChevronRight, Star, HandCoins } from 'lucide-react'
import { cn, formatKickoff, formatOdds, formatProb, liveScoreLabel, probabilityDisplay, resolvePickBadgeStatus } from '@/lib/utils'
import { ConfidenceBadge } from './ConfidenceBadge'
import { RiskBadge } from './RiskBadge'
import { StatusBadge } from './StatusBadge'
import { TeamCrest } from './TeamCrest'
import { RunBadge } from './RunBadge'
import { RegisterBetModal } from './RegisterBetModal'
import { PlayedBadge } from './PlayedBadge'
import { useViewSettings } from '@/hooks/useViewSettings'
import type { PickLeg } from '@/types/miniApp'

export function SecondaryPickCard({
  pick,
  onSelect,
}: {
  pick: PickLeg
  onSelect?: () => void
}) {
  const { isFavorite, toggleFavorite } = useViewSettings()
  const [showPlayModal, setShowPlayModal] = useState(false)
  const bettable = pick.is_bettable !== false
  const recordIdStr = String(pick.decision_record_id)
  const isFav = isFavorite(recordIdStr)
  // P1.10 (25/09/2026, Secciones 1/2 del encargo): MISMA lectura canónica de estado que
  // TopPickCard/Home/Actividad reciente -- nunca una segunda derivación local.
  const badgeStatus = resolvePickBadgeStatus(pick.fixture_status, pick.settled_status)
  const isLive = pick.fixture_status === 'live'
  const liveScore = isLive ? liveScoreLabel(pick.home_score, pick.away_score) : null

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    toggleFavorite(recordIdStr)
  }

  return (
    <div
      onClick={onSelect}
      className="card cursor-pointer bg-panel border-border hover:border-text-dim/40 active-scale hover-float p-4 space-y-3.5"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-black uppercase tracking-wider text-text-dim bg-panel-elevated px-2 py-0.5 rounded border border-border">
            Pick Secundario
          </span>
          {pick.run_type && <RunBadge runType={pick.run_type} compact />}
        </div>
        <div className="flex items-center gap-2">
          {badgeStatus && <StatusBadge status={badgeStatus} />}
          <span className="text-[10px] font-bold text-text-dim">
            {formatKickoff(pick.kickoff_utc, { withWeekday: true })}
          </span>
          <button
            onClick={handleFavoriteClick}
            className="p-1 rounded-lg bg-panel-elevated hover:bg-border text-text-dim hover:text-brand transition"
          >
            <Star
              className={cn('h-3.5 w-3.5', isFav ? 'fill-brand text-brand' : 'text-text-dim')}
            />
          </button>
        </div>
      </div>

      {/* Match details */}
      <div className="flex items-center gap-3">
        <div className="flex -space-x-1 shrink-0 bg-panel-elevated p-1 rounded-full border border-border">
          <TeamCrest src={pick.home_team_logo} alt={pick.home_team ?? 'Local'} size={20} />
          <TeamCrest src={pick.away_team_logo} alt={pick.away_team ?? 'Visitante'} size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-extrabold text-text truncate">
            {pick.match_label ?? `${pick.home_team} vs ${pick.away_team}`}
          </p>
          {pick.league && (
            <p className="text-[9px] font-bold uppercase tracking-wider text-text-dim truncate">
              {pick.league}
            </p>
          )}
          {isLive && liveScore && (
            <p className="mt-0.5 text-[10px] font-mono font-black text-brand">{liveScore}</p>
          )}
          {pick.settled_status && pick.score_display && pick.score_display !== 'Pendiente' && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <span className="text-[9px] font-black uppercase text-brand bg-brand-soft/20 border border-brand/20 px-2 py-0.5 rounded-lg inline-block">
                Resultado final: {pick.score_display}
              </span>
              {/* P0.32 (26/08/2026, rediseño Mini App -- Sección 3 del encargo): resultado
                  real del mercado de ESTE pick, no solo el marcador general del partido. */}
              {pick.market_result_display && (
                <span className="text-[9px] font-black uppercase text-cyan bg-cyan/10 border border-cyan/20 px-2 py-0.5 rounded-lg inline-block">
                  {pick.market_result_display}
                </span>
              )}
              {pick.stat_result_display && (
                <span className="text-[9px] font-black uppercase text-cyan bg-cyan/10 border border-cyan/20 px-2 py-0.5 rounded-lg inline-block">
                  {pick.stat_result_display}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Market and Odds */}
      <div className="flex items-center justify-between gap-3 pt-1.5 border-t border-border/40">
        <div>
          <p className="text-xs font-bold text-brand uppercase tracking-wide">
            {pick.selection_label || pick.selection}
          </p>
        </div>
        <div className="text-right">
          <p className="text-lg font-black text-text tabular-nums leading-none">
            {formatOdds(pick.odds)}
          </p>
          {/* Sección E (25/09/2026, "Odds Truth"): ver TopPickCard.tsx -- `pick.odds` está
              congelada junto con `reference_bookmaker`, nunca con `pick.bookmaker` (lectura en
              vivo, independiente). El nombre pegado al número debe ser al que pertenece. */}
          {(pick.reference_bookmaker ?? pick.bookmaker) && (
            <p className="text-[8px] font-bold text-text-dim uppercase mt-0.5">
              {pick.reference_bookmaker ?? pick.bookmaker}
            </p>
          )}
        </div>
      </div>
      {(pick.odds_captured_at || (pick.bookmaker && pick.reference_bookmaker && pick.bookmaker !== pick.reference_bookmaker)) && (
        <div className="flex items-center gap-3 text-[8px] text-text-dim pt-0.5">
          {pick.odds_captured_at && (
            <span>Capturada: {formatKickoff(pick.odds_captured_at, { withWeekday: false })}</span>
          )}
          {pick.bookmaker && pick.reference_bookmaker && pick.bookmaker !== pick.reference_bookmaker && (
            <span>Actualmente en {pick.bookmaker}</span>
          )}
        </div>
      )}

      {!bettable && pick.invalid_reason && !pick.settled_status && (
        <p className="text-[10px] font-semibold text-danger bg-danger-soft/20 border border-danger/10 rounded-lg px-2.5 py-1">
          ⚠️ {pick.invalid_reason.toUpperCase().trim() === 'LEG_ALREADY_STARTED' 
            ? 'El partido ya ha comenzado' 
            : pick.invalid_reason.toUpperCase().trim() === 'LEG_FINISHED' 
            ? 'El partido ya ha finalizado' 
            : pick.invalid_reason.replace(/_/g, ' ').toLowerCase()}
        </p>
      )}

      {/* Badges */}
      <div className="grid grid-cols-2 gap-3 pt-1">
        <ConfidenceBadge score={pick.confidence_score} />
        <RiskBadge level={pick.risk_level} />
      </div>

      {/* P0.26.1 (Sección G del encargo): antes condicionado a `viewMode === 'expert'` --
          con el toggle Simple/Experto retirado (P0.26) ese modo ya no tiene forma de
          cambiarse, y un cliente que lo hubiera dejado en 'simple' antes del deploy jamás
          volvería a ver este bloque. Se muestra siempre para garantizar una única
          experiencia real. */}
      {/* Sprint "Product Experience V2" (05/09/2026, Sección 3 del encargo): "elimina
          lenguaje interno/técnico que no ayude al cliente" -- el Valor Esperado (EV) como
          número suelto se retira de esta card (jerga de trading, no está en la lista de
          datos realmente útiles del encargo); Probabilidad sigue visible, honesta ante
          fallback/desconocido (`probabilityDisplay`). */}
      <div className="pt-2 border-t border-border/40 text-[11px] text-text-muted">
        <span className="text-[8px] font-bold uppercase text-text-dim block">Probabilidad IA</span>
        {/* Sprint "Corrección Puntual -- Labs + Probabilidad Cliente" (10/09/2026, Sección 3
            del encargo): BUG REAL corregido -- `fallback` sustituía el número real por
            "Confianza alta/moderada" bajo la etiqueta "Probabilidad IA" (dato distinto,
            nunca intercambiable). `probabilityDisplay` ya expone un `label` real también en
            `fallback` (ver `lib/utils.ts`) -- se muestra igual que `calibrated`, nunca se
            inventa ni se omite un número real. */}
        {(() => {
          const p = probabilityDisplay(pick.model_probability, pick.probability_is_calibrated)
          return p.kind === 'unknown' ? (
            <span className="font-extrabold text-text">{formatProb(null)}</span>
          ) : (
            <span className="font-extrabold text-text">{p.label}</span>
          )
        })()}
      </div>

      <div className="flex items-center justify-between pt-0.5 text-[10px] font-bold text-brand hover:text-accent-hover transition-colors">
        <span>Ver detalles del pick</span>
        <ChevronRight className="h-3.5 w-3.5" />
      </div>

      {/* Fase R.13 (17/08/2026): "LA JUGUÉ" registra una apuesta que el usuario YA hizo por su
          cuenta -- nunca depende de `is_bettable`/`settled_status` (partido en vivo/finalizado
          no debe impedir registrar una apuesta real hecha horas antes). Solo depende de si ya
          se registró (`my_bet`). */}
      {pick.my_bet ? (
        <PlayedBadge bet={pick.my_bet} />
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation()
            setShowPlayModal(true)
          }}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-panel-elevated border border-border/60 text-text-dim hover:text-brand hover:border-brand/40 text-[10px] font-black uppercase tracking-wider transition"
        >
          <HandCoins className="h-3.5 w-3.5" />
          La jugué
        </button>
      )}

      {showPlayModal && (
        <RegisterBetModal
          onClose={() => setShowPlayModal(false)}
          category="top_ia"
          matchLabel={pick.match_label ?? `${pick.home_team} vs ${pick.away_team}`}
          selectionLabel={pick.selection_label || pick.selection || ''}
          referenceOdds={pick.odds ?? 0}
          decisionRecordId={pick.decision_record_id}
        />
      )}
    </div>
  )
}
